import React, { useEffect, useState } from "react";
import {
  Search,
  Eye,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  useGetAssetQuery,
  useHandleAssetDeletionMutation,
} from "../../../slices/assetApiSlice";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DeletionApproval = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalData, setModalData] = useState(null);
  const { data: assets, refetch } = useGetAssetQuery();
  const [data, setData] = useState([]);
  const [isDeclining, setIsDeclining] = useState(false);
  const [handleAssetDeletion, { isLoading }] = useHandleAssetDeletionMutation();
  const [loadingApprove, setLoadingApprove] = useState(false);

  useEffect(() => {
    if (assets) {
      const filteredAssets = assets.filter(
        (asset) => asset.status === "Pending" && asset.deleted === true,
      );
      setData(filteredAssets);
    }
  }, [assets]);

  const rowsPerPage = 10;
  const sortedData = [...data].sort((a, b) => b.rid - a.rid);

  const filteredData = sortedData.filter((item) => {
    const matchesSearch = Object.values(item).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleView = (item) => {
    setModalData(item);
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const handleApprove = async (e) => {
    e.preventDefault();

    const assetCode = modalData.assetCode;
    const email = modalData.deletedBy;
    const action = "accept";

    toast("Are you sure?", {
      description: "You are about to approve this asset deletion request.",
      action: {
        label: "Yes, approve it!",
        onClick: async () => {
          try {
            setLoadingApprove(true);
            const response = await handleAssetDeletion({
              assetCode,
              email,
              action,
            }).unwrap();
            refetch();
            setLoadingApprove(false);

            toast.success("Asset deletion approved successfully!");
            handleCloseModal();
          } catch (err) {
            setLoadingApprove(false);
            toast.error("Approval Failed", {
              description:
                err?.data?.message ||
                "Unable to approve the asset at this time. Please try again later.",
            });
          }
        },
      },
      cancel: {
        label: "Cancel",
      },
    });
  };

  const handleDecline = async (e) => {
    e.preventDefault();

    const assetCode = modalData.assetCode;
    const email = modalData.deletedBy;
    const action = "decline";

    toast("Are you sure?", {
      description: "You are about to decline this asset deletion request.",
      action: {
        label: "Yes, decline it!",
        onClick: async () => {
          try {
            setIsDeclining(true);
            const response = await handleAssetDeletion({
              assetCode,
              email,
              action,
            }).unwrap();
            refetch();
            setIsDeclining(false);

            toast.success("Asset deletion declined successfully!");
            handleCloseModal();
          } catch (err) {
            setIsDeclining(false);
            toast.error("Decline Failed", {
              description:
                err?.data?.message ||
                "An error occurred while declining the asset. Please try again later.",
            });
          }
        },
      },
      cancel: {
        label: "Cancel",
      },
    });
  };

  const handleSelectRow = (assetCode) => {
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(assetCode)) {
        return prevSelected.filter((code) => code !== assetCode);
      } else {
        return [...prevSelected, assetCode];
      }
    });
  };

  const handleSelectAllRows = (e) => {
    if (e.target.checked) {
      const allAssetCodes = displayedData.map((item) => item.assetCode);
      setSelectedRows(allAssetCodes);
    } else {
      setSelectedRows([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="flex flex-col flex-1">
      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search deletion approvals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === displayedData.length &&
                      displayedData.length > 0
                    }
                    onChange={handleSelectAllRows}
                    className="rounded border-gray-300"
                  />
                </TableHead>
                <TableHead className="font-semibold">Asset Code</TableHead>
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Area</TableHead>
                <TableHead className="font-semibold">Requested By</TableHead>
                <TableHead className="font-semibold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow key={item.assetCode || index}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.assetCode)}
                        onChange={() => handleSelectRow(item.assetCode)}
                        className="rounded border-gray-300"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.assetCode}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell>{item.assetArea}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.deletedBy}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(item)}
                        className="gap-2"
                        style={{
                          backgroundColor:
                            "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                          color: "white",
                        }}
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No pending deletion approvals
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-2 py-4">
            <p className="text-sm text-muted-foreground">
              Showing{" "}
              {displayedData.length > 0
                ? (currentPage - 1) * rowsPerPage + 1
                : 0}{" "}
              to {Math.min(currentPage * rowsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* First Page */}
              <Button
                variant={currentPage === 1 ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(1)}
                className="w-8 aspect-square p-0"
                style={currentPage === 1 ? { backgroundColor: "#305845" } : {}}
              >
                1
              </Button>

              {/* Ellipsis Logic */}
              {(() => {
                const pages = [];
                let start = Math.max(2, currentPage - 1);
                let end = Math.min(totalPages - 1, currentPage + 1);

                // Adjust range if close to boundaries
                if (currentPage <= 3) {
                  end = Math.min(4, totalPages - 1);
                }
                if (currentPage >= totalPages - 2) {
                  start = Math.max(totalPages - 3, 2);
                }

                if (start > 2) {
                  pages.push(
                    <span
                      key="start-ellipsis"
                      className="px-2 text-muted-foreground flex items-center justify-center w-8"
                    >
                      ...
                    </span>,
                  );
                }

                for (let i = start; i <= end; i++) {
                  pages.push(
                    <Button
                      key={i}
                      variant={currentPage === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(i)}
                      className="w-8 aspect-square p-0"
                      style={
                        currentPage === i ? { backgroundColor: "#305845" } : {}
                      }
                    >
                      {i}
                    </Button>,
                  );
                }

                if (end < totalPages - 1) {
                  pages.push(
                    <span
                      key="end-ellipsis"
                      className="px-2 text-muted-foreground flex items-center justify-center w-8"
                    >
                      ...
                    </span>,
                  );
                }

                return pages;
              })()}

              {/* Last Page */}
              {totalPages > 1 && (
                <Button
                  variant={currentPage === totalPages ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-8 aspect-square p-0"
                  style={
                    currentPage === totalPages
                      ? { backgroundColor: "#305845" }
                      : {}
                  }
                >
                  {totalPages}
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal for Viewing Request */}
      <Dialog
        open={!!modalData}
        onOpenChange={(open) => !open && handleCloseModal()}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Asset Deletion Request</DialogTitle>
          </DialogHeader>

          {modalData && (
            <div className="space-y-4">
              {/* Common fields for all asset types */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Asset ID</Label>
                  <Input
                    type="text"
                    value={modalData.assetID}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Asset Code</Label>
                  <Input
                    type="text"
                    value={modalData.assetCode}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  type="text"
                  value={modalData.title || ""}
                  readOnly
                  className="bg-muted"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cost</Label>
                  <Input
                    type="text"
                    value={modalData.cost || ""}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Depreciated Value</Label>
                  <Input
                    value={modalData.categoryDetails?.depreciatedValue || "N/A"}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>

              {/* Building specific fields */}
              {modalData.categoryDetails?.name === "Building" && (
                <>
                  <div className="space-y-2">
                    <Label>Plinth Area</Label>
                    <Input
                      type="text"
                      value={
                        modalData.attributes?.find(
                          (attr) => attr.name === "Plint_area",
                        )?.value || "N/A"
                      }
                      readOnly
                      className="bg-muted"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Floors & Rooms</Label>
                    <div className="p-3 border rounded-md bg-muted/50 space-y-1 max-h-32 overflow-y-auto">
                      {(() => {
                        const floorAttr = modalData.attributes?.find(
                          (attr) => attr.name === "Floor and rooms",
                        );
                        if (floorAttr) {
                          try {
                            const floorData = JSON.parse(floorAttr.value);
                            return Object.entries(floorData).map(
                              ([floorName, rooms]) => (
                                <div key={floorName} className="text-sm">
                                  <strong>{floorName}</strong>:{" "}
                                  {rooms.join(", ")}
                                </div>
                              ),
                            );
                          } catch (error) {
                            return (
                              <span className="text-sm">
                                Invalid floor data
                              </span>
                            );
                          }
                        }
                        return (
                          <span className="text-sm text-muted-foreground">
                            N/A
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}

              {/* Machinery specific fields */}
              {modalData.categoryDetails?.name === "Machinery" && (
                <div className="space-y-2">
                  <Label>Serial Number</Label>
                  <Input
                    type="text"
                    value={
                      modalData.attributes?.find(
                        (attr) => attr.name === "Serial_number",
                      )?.value || "N/A"
                    }
                    readOnly
                    className="bg-muted"
                  />
                </div>
              )}

              {/* Landscaping/Facility/Infrastructure specific fields */}
              {(modalData.categoryDetails?.name === "Landscaping" ||
                modalData.categoryDetails?.name === "Facility" ||
                modalData.categoryDetails?.name === "Infrastructure") && (
                <div className="space-y-2">
                  <Label>Size</Label>
                  <Input
                    type="text"
                    value={
                      modalData.attributes?.find((attr) => attr.name === "Size")
                        ?.value || "N/A"
                    }
                    readOnly
                    className="bg-muted"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Acquired Date</Label>
                  <Input
                    type="text"
                    value={formatDate(modalData.acquireDate) || ""}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Useful Life (Years)</Label>
                  <Input
                    type="text"
                    value={modalData.lifespan || ""}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Badge variant="warning">{modalData.status}</Badge>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    value={modalData.categoryDetails?.name || "N/A"}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Area</Label>
                  <Input
                    type="text"
                    value={modalData.assetArea || ""}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deletion Requested By</Label>
                  <Input
                    value={modalData.deletedBy}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={modalData.description || ""}
                  readOnly
                  rows={3}
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label>Deletion Reason</Label>
                <Textarea
                  value={modalData.deletionReason || "Not provided"}
                  readOnly
                  rows={2}
                  className="bg-muted"
                />
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleApprove}
                  disabled={loadingApprove}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loadingApprove ? "Approving..." : "Approve Deletion"}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDecline}
                  disabled={isDeclining}
                >
                  {isDeclining ? "Declining..." : "Decline"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeletionApproval;
